import type { FC } from 'react'

import { useEffect } from 'react'
import { useRouter } from 'next/router'
import Lenis from 'lenis'
import { gsap, ScrollTrigger, prefersReducedMotion } from '@/lib/motion'
import {
    getLenis,
    scrollToHash,
    scrollToTarget,
    setLenis
} from '@/lib/smooth-scroll'

/**
 * Owns page scrolling: Lenis smoothing (skipped for reduced motion), in-page anchor
 * clicks, hash targets after navigation, and ScrollTrigger refreshes once fonts load.
 */
export const SmoothScroll: FC = () => {
    const router = useRouter()

    useEffect(() => {
        if (prefersReducedMotion()) return

        const lenis = new Lenis({
            lerp: 0.1,
            smoothWheel: true,
            syncTouch: false,
            autoRaf: false
        })

        const tick = (time: number) => lenis.raf(time * 1000)

        setLenis(lenis)
        lenis.on('scroll', ScrollTrigger.update)
        gsap.ticker.add(tick)
        gsap.ticker.lagSmoothing(0)

        return () => {
            gsap.ticker.remove(tick)
            lenis.destroy()
            setLenis(null)
        }
    }, [])

    useEffect(() => {
        const onClick = (event: MouseEvent) => {
            if (event.defaultPrevented || event.button !== 0) return
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

            const anchor = (event.target as HTMLElement | null)?.closest('a')

            if (!anchor || anchor.target === '_blank') return

            const href = anchor.getAttribute('href')

            if (!href || !href.includes('#')) return

            const url = new URL(anchor.href, window.location.href)

            if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return
            if (!url.hash || url.hash === '#') return

            const target = document.getElementById(decodeURIComponent(url.hash.slice(1)))

            if (!target) return

            event.preventDefault()
            scrollToTarget(target, { focus: true })
            window.history.replaceState(window.history.state, '', url.hash)
        }

        document.addEventListener('click', onClick)

        return () => document.removeEventListener('click', onClick)
    }, [])

    useEffect(() => {
        const onStart = () => document.documentElement.classList.add('intro-done')

        const onComplete = (url: string) => {
            getLenis()?.resize()

            window.requestAnimationFrame(() => {
                ScrollTrigger.refresh()

                const hash = url.split('#')[1]

                if (hash) window.setTimeout(() => scrollToHash(hash, { immediate: true }), 60)
            })
        }

        router.events.on('routeChangeStart', onStart)
        router.events.on('routeChangeComplete', onComplete)

        return () => {
            router.events.off('routeChangeStart', onStart)
            router.events.off('routeChangeComplete', onComplete)
        }
    }, [router.events])

    useEffect(() => {
        const refresh = () => ScrollTrigger.refresh()

        document.fonts?.ready.then(refresh).catch(() => undefined)
        window.addEventListener('load', refresh)

        if (window.location.hash) {
            const hash = window.location.hash

            window.setTimeout(() => scrollToHash(hash, { immediate: true }), 250)
        }

        return () => window.removeEventListener('load', refresh)
    }, [])

    return null
}
